import {
  Component, h, Prop, State, Watch,
} from '@stencil/core';
import { ExpAPI } from '@pes-ui/components/dist/types/global-types/api/exp-api';
import { CategoriesApi, ACTION_BLOCK_TYPES, buildPageViewPayload, send } from '@pes-ui/utils';
import type { TScProductSelectorConfig } from '@pes-ui/utils';

import { expAPIEndpoint, getExpAPIEndpointURL } from '../../../globals/utils/exp-api-urls-builder';
import { BREADCRUMBS_ITEM_TYPE, getData } from '../../../globals/utils';

@Component({
  tag: 'pes-app-spryker-all-products-container',
  shadow: true,
})
export class PesAppSprykerAllProductsContainer {
  /**
   * The code of the country of the app.
   * @example fr
   */
  @Prop() country!: string;

  /**
   * The code of the language of the app.
   * @example fr
   */
  @Prop() language!: string;

  @Prop() host!: string;

  @Prop() endpoint!: string;

  /**
   * Show breadcrumbs on PES all products page.
   */
  @Prop() showBreadcrumbs: boolean;

  /**
   * This parameter contains configuration for the document download center url
   * displayed as an action block on All Products page.
   * For more information, please visit
   * https://confluence.se.com/display/PESV3/SE+All+Products+component.
   */
  @Prop() ddcAllProductsUrl?: string;

  @State() data: ExpAPI.getAllProductsData;

  @State() atgBusinesses?: Array<CategoriesApi.ProductsAllBusinesses>;

  // Action blocks state used for updating the All Products page
  @State() appActionBlocks: CategoriesApi.ActionBlocksHashMap = {};

  // Action blocks private property used for storing default action blocks values
  public defaultAppActionBlocks: CategoriesApi.ActionBlocksHashMap = {};

  emitPageViewEvent() {
    send(
      buildPageViewPayload,
      {
        pageLanguage: this.language,
        pageTitle: this.data.metaTags.analyticsPageData.pageTitle,
        fromWebView: this.data.metaTags.analyticsPageData.fromWebView,
        businessUnit: this.data.metaTags.analyticsPageData.businessUnit,
        template: this.data.metaTags.analyticsPageData.template,
        pageTopCategory: this.data.metaTags.analyticsPageData.pageTopCategory,
        pageCategory: this.data.metaTags.analyticsPageData.pageCategory,
        pageSubCategory: this.data.metaTags.analyticsPageData.pageSubCategory,
        pageSubSubCategory: this.data.metaTags.analyticsPageData.pageSubSubCategory,
        pageCategoryId: this.data.metaTags.analyticsPageData.pageCategoryId,
        pageSubCategoryId: this.data.metaTags.analyticsPageData.pageSubCategoryId,
        pageRangeId: this.data.metaTags.analyticsPageData.pageRangeId,
        pageProductId: this.data.metaTags.analyticsPageData.pageProductId,
      },
    );
  }

  async componentWillLoad(): Promise<void> {
    await this.loadAllProductsData();
    if (this.data?.metaTags?.analyticsPageData) this.emitPageViewEvent();
  }

  @Watch('ddcAllProductsUrl')
  watchPropHandler(newUrlValue: string) {
    this.setDDCActionBlockUrl(newUrlValue);
  }

  public getFilteredActionBlocks(keys: Array<string>): CategoriesApi.ActionBlocksHashMap {
    const filteredActionBlocks = {};

    keys.forEach((key) => {
      if (!(key in this.defaultAppActionBlocks)) return;
      filteredActionBlocks[key] = this.defaultAppActionBlocks[key];
    });

    return filteredActionBlocks;
  }

  public setDDCActionBlockUrl(ddcUrl: string) {
    if (ddcUrl) {
      // Reset appActionBlocks to default value, filtered by Document Download Center
      this.appActionBlocks = this.getFilteredActionBlocks([ACTION_BLOCK_TYPES.DOWNLOAD_CENTER]);
      // Save new URL provided by consumers to the DDC Action block
      this.appActionBlocks[ACTION_BLOCK_TYPES.DOWNLOAD_CENTER].link = ddcUrl;
      // Update appActionBlocks State to force rerender (rewrite with a copy)
      this.appActionBlocks = { ...this.appActionBlocks };
    } else {
      // Update appActionBlocks State to force rerender (clearing hides action blocks)
      this.appActionBlocks = {};
    }
    this.data.main.appActionBlocks = this.appActionBlocks;
  }

  async loadAllProductsData() {
    const allProductsEndpointURL = getExpAPIEndpointURL(expAPIEndpoint.ALL_PRODUCTS_SPRYKER);
    try {
      this.data = await getData(false, allProductsEndpointURL);
      // Save default action blocks value from PES BE
      this.defaultAppActionBlocks = { ...this.data.main.appActionBlocks };
      // Reset action blocks to the state supported by PES Business Service (PBS)
      this.setDDCActionBlockUrl(this.ddcAllProductsUrl);
    } catch (error) {
      console.error('loading all products failed ', error);
    }
  }

  private getProductSelectorConfig = (): Promise<TScProductSelectorConfig> => {
    const productSelectorEndpointURL = getExpAPIEndpointURL(
      expAPIEndpoint.PRODUCT_SELECTOR_SPRYKER,
    );
    return getData(false, productSelectorEndpointURL);
  };

  get customBreadcrumbs() {
    if (!this.data?.breadcrumbs?.breadcrumbs) return null;

    return this.data?.breadcrumbs?.breadcrumbs.filter(
      (breadcrumb) => !BREADCRUMBS_ITEM_TYPE.includes(breadcrumb.itemType),
    );
  }

  render(): HTMLElement {
    if (!this.data) return null;

    return (
      <pes-all-products
        host={this.host}
        {...this.data?.main}
        showBreadcrumbs={this.showBreadcrumbs}
        breadcrumbs={this.customBreadcrumbs}
        hideOnMobile={this.data?.breadcrumbs?.hideOnMobile}
        categoriesUrl={this.data?.main?.categoriesUrl}
        getProductSelectorConfig={this.getProductSelectorConfig}
        hideMasterRangesBanner={true}
        isPBS={true}
        languageCode={this.language}
        countryCode={this.country}
      />
    );
  }
}
